import React, { useState } from "react";
import { checkEligibility, errMessage } from "@/lib/api";
import { PRODUCTS } from "@/data/site";
import { Loader2, Check, X, ArrowRight, ArrowLeft } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const steps = ["Profile", "Income", "Loan", "Result"];

export default function Eligibility() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    employment_type: "self_employed",
    age: 30,
    monthly_income: 60000,
    existing_emi: 0,
    credit_score: 700,
    loan_amount: 1000000,
    tenure_months: 48,
    loan_product: PRODUCTS[0].name,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });

  async function submit() {
    setErr(""); setBusy(true);
    try { setResult(await checkEligibility(form)); setStep(3); }
    catch (e) { setErr(errMessage(e)); }
    finally { setBusy(false); }
  }

  return (
    <main data-testid="eligibility-page" className="pt-24 md:pt-32 pb-24">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chapter-index text-emerald-700">Eligibility · Two-minute check</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">Am I <span className="italic text-emerald-800">eligible?</span></h1>

        <div className="mt-10 rounded-3xl bg-white border border-black/[0.06] p-8 md:p-10">
          <div className="flex items-center gap-4">
            <Progress value={((step + 1) / steps.length) * 100} className="h-1"/>
            <p className="text-sm text-slate-500 font-mono">{step + 1}/{steps.length}</p>
          </div>
          <p className="mt-6 chapter-index text-slate-500">Step · {steps[step]}</p>

          {step === 0 && (
            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Employment type">
                <select data-testid="elig-employment" value={form.employment_type} onChange={on('employment_type')} className="input">
                  <option value="salaried">Salaried</option>
                  <option value="self_employed">Self Employed</option>
                  <option value="business">Business Owner</option>
                  <option value="farmer">Farmer</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Age"><input data-testid="elig-age" type="number" value={form.age} onChange={on('age')} className="input"/></Field>
              <Field label="Credit score (approx)"><input data-testid="elig-cibil" type="number" value={form.credit_score} onChange={on('credit_score')} className="input"/></Field>
            </div>
          )}

          {step === 1 && (
            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Monthly income (₹)"><input data-testid="elig-income" type="number" value={form.monthly_income} onChange={on('monthly_income')} className="input"/></Field>
              <Field label="Existing EMIs (₹)"><input data-testid="elig-emi" type="number" value={form.existing_emi} onChange={on('existing_emi')} className="input"/></Field>
            </div>
          )}

          {step === 2 && (
            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Loan product">
                <select data-testid="elig-product" value={form.loan_product} onChange={on('loan_product')} className="input">
                  {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Loan amount (₹)"><input data-testid="elig-amount" type="number" value={form.loan_amount} onChange={on('loan_amount')} className="input"/></Field>
              <Field label="Tenure (months)"><input data-testid="elig-tenure" type="number" value={form.tenure_months} onChange={on('tenure_months')} className="input"/></Field>
            </div>
          )}

          {step === 3 && result && (
            <div data-testid="elig-result" className="mt-6">
              <div className={`rounded-2xl p-6 ${result.eligible ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full grid place-items-center ${result.eligible ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                    {result.eligible ? <Check size={20}/> : <X size={20}/>}
                  </div>
                  <p className="font-display text-2xl">{result.eligible ? 'Indicative pre-check passed.' : 'Needs review.'}</p>
                </div>
                <div className="mt-6 grid sm:grid-cols-3 gap-4">
                  <Stat label="Est. Monthly EMI" v={`₹${result.monthly_emi_estimate.toLocaleString('en-IN')}`}/>
                  <Stat label="Max Loan Estimate" v={`₹${result.max_loan_estimate.toLocaleString('en-IN')}`}/>
                  <Stat label="FOIR" v={`${(result.foir_ratio*100).toFixed(1)}%`}/>
                </div>
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Notes</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">{result.reasons.map((r,i)=>(<li key={i}>{r}</li>))}</ul>
                </div>
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Tips</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">{result.tips.map((r,i)=>(<li key={i}>{r}</li>))}</ul>
                </div>
                <p className="mt-4 text-[11px] text-slate-500">{result.disclaimer}</p>
              </div>
            </div>
          )}

          {err && <p className="mt-4 text-sm text-red-600">{err}</p>}

          <div className="mt-8 flex items-center justify-between">
            <button data-testid="elig-back" disabled={step === 0} onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-sm text-slate-700 disabled:opacity-30"><ArrowLeft size={14}/> Back</button>
            {step < 2 && (
              <button data-testid="elig-next" onClick={() => setStep(step + 1)} className="btn-line inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm">Next <ArrowRight size={14}/></button>
            )}
            {step === 2 && (
              <button data-testid="elig-submit" onClick={submit} disabled={busy} className="btn-line inline-flex items-center gap-2 rounded-full bg-emerald-700 text-white px-5 py-2.5 text-sm">
                {busy ? <Loader2 className="animate-spin" size={14}/> : <>Check Eligibility <ArrowRight size={14}/></>}
              </button>
            )}
            {step === 3 && (
              <a href="/apply" className="btn-line inline-flex items-center gap-2 rounded-full bg-emerald-700 text-white px-5 py-2.5 text-sm">Apply for a Loan <ArrowRight size={14}/></a>
            )}
          </div>
        </div>
      </section>
      <style>{`.input{width:100%;border:1px solid hsl(214 32% 91%);background:#fff;border-radius:1rem;padding:.75rem 1rem;font-size:.9rem;outline:none}.input:focus{border-color:hsl(158 64% 40%)}`}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
function Stat({ label, v }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="font-display text-2xl text-slate-900 mt-1">{v}</p>
    </div>
  );
}
